import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useSearch } from '@tanstack/react-router';
import { BarChart3, Settings } from 'lucide-react';
import { ResultsView } from '@/components/organisms';
import { Spinner } from '@lean-agility/design-system/react';
import { useSimulation } from '@/hooks/queries/useSimulationQueries';
import { useLayoutContext } from '@/contexts/useLayoutContext';
import { getSimulationResult } from '@/lib/api';
import {
  reorderDeliverableResults,
  simulationResponseToFormValues,
  getTeamColor,
  resolveSimulationStart,
} from '@/lib/utils';
import type { OrganizationMonteCarloResponse } from '@/types/api';

export function ResultsPage() {
  const { simulationId } = useParams({ from: '/simulation/$simulationId/results' });
  const currentSearch = useSearch({ from: '/simulation/$simulationId/results' });
  const { setSelectedSimulation, setResultsInfo, resultsVersion } = useLayoutContext();

  const [result, setResult] = useState<OrganizationMonteCarloResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track the resultsVersion we've loaded to detect when new results are available
  // Start at -1 to indicate initial load hasn't happened yet
  const loadedVersionRef = useRef<number>(-1);

  // Fetch simulation data for team colors and ordering (pre-loaded by route loader)
  const { data: simulationData } = useSimulation(simulationId);
  const formData = simulationData ? simulationResponseToFormValues(simulationData) : null;

  // Update layout context when simulation data loads
  useEffect(() => {
    if (simulationData) {
      setSelectedSimulation(simulationId, simulationData.name);
    }
  }, [simulationData, simulationId, setSelectedSimulation]);

  // Load results from sessionStorage or API
  const loadResults = useCallback(
    async (useSessionStorage: boolean) => {
      setIsLoading(true);

      // Try sessionStorage first (populated by ConfigPage for instant display)
      if (useSessionStorage) {
        const cached = sessionStorage.getItem(`simulation-result-${simulationId}`);
        if (cached) {
          try {
            const parsed = JSON.parse(cached) as OrganizationMonteCarloResponse;
            setResult(parsed);
            setResultsInfo(true, parsed.deliverables.length);
            setIsLoading(false);
            return;
          } catch {
            // Invalid cache, continue to API
          }
        }
      }

      // Fetch from API
      try {
        const apiResult = await getSimulationResult(simulationId);
        // Compute formData inside effect to avoid dependency on unstable reference
        const currentFormData = simulationData ? simulationResponseToFormValues(simulationData) : null;
        if (apiResult && currentFormData) {
          // Reorder results to match form data order
          const teamOrder = currentFormData.teams.map((team) => team.name);
          const teamDeliverableOrder = Object.fromEntries(
            currentFormData.teams.map((team) => [team.name, team.deliverables.map((d) => d.name)]),
          );
          const reordered = reorderDeliverableResults(apiResult.deliverables, teamOrder, teamDeliverableOrder);
          const orderedResult = { ...apiResult, deliverables: reordered };

          setResult(orderedResult);
          setResultsInfo(true, reordered.length);

          // Cache API result in sessionStorage for future page revisits.
          // Best-effort: may fail for large results (QuotaExceededError).
          try {
            sessionStorage.setItem(`simulation-result-${simulationId}`, JSON.stringify(orderedResult));
          } catch {
            // Silently ignore - we'll fetch from API again if needed
          }
        }
      } catch (err) {
        console.error('Failed to load results:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [simulationId, simulationData, setResultsInfo],
  );

  // Load results on mount and when new results become available
  useEffect(() => {
    if (!simulationId) return;

    const isInitialLoad = loadedVersionRef.current === -1;
    const hasNewResults = resultsVersion > loadedVersionRef.current;

    if (isInitialLoad) {
      // Initial load: try sessionStorage first, fall back to API
      loadResults(true);
      loadedVersionRef.current = resultsVersion;
    } else if (hasNewResults) {
      // New results available: reload from API (sessionStorage may not be updated yet)
      loadResults(false);
      loadedVersionRef.current = resultsVersion;
    }
  }, [simulationId, resultsVersion, loadResults]);

  // Feature due dates map (feature name → due date) - must be called before early returns
  const featureDueDates = useMemo(() => {
    if (!formData) return new Map<string, Date | null>();
    return new Map(formData.features.map((f) => [f.name, f.due_date ? new Date(f.due_date) : null]));
  }, [formData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="border-theme bg-theme-elevated/50 rounded-xl border p-12 text-center backdrop-blur-sm">
          <BarChart3 className="mx-auto mb-4 size-12 text-text-subtle" />
          <h3 className="text-theme-primary mb-2 text-lg font-semibold">No Simulation Results Yet</h3>
          <p className="text-theme-muted mb-6">
            Run the simulation from the Configuration tab to see delivery forecasts.
          </p>
          <Link
            to="/simulation/$simulationId"
            params={{ simulationId }}
            search={currentSearch}
            className="
              inline-flex items-center gap-2 rounded-lg bg-action-primary px-4 py-2 font-medium text-white
              transition-colors
              hover:bg-action-primary-hover
            "
          >
            <Settings className="size-4" />
            Go to Configuration
          </Link>
        </div>
      </div>
    );
  }

  // Build team colors map
  const teamColors = formData
    ? Object.fromEntries(formData.teams.map((team, index) => [team.name, getTeamColor(index, team.color)]))
    : {};

  // Feature names in configuration order (for consistent ordering in results)
  const featureOrder = formData ? formData.features.map((f) => f.name) : [];

  // Pass teams from simulation data for dependency visualization
  const teams = simulationData?.teams;

  // Effective forecast start: a null start_date means "start today" (backend
  // default), so resolve it here to anchor due-date-before-start warnings (S3).
  const simulationStartDate = resolveSimulationStart(simulationData?.start_date);

  return (
    <ResultsView
      result={result}
      teamColors={teamColors}
      featureOrder={featureOrder}
      featureDueDates={featureDueDates}
      teams={teams}
      simulationStartDate={simulationStartDate}
    />
  );
}
